import {
  ISpritePointerEvent, Container, Template, Rectangle, color, percent, Root, Bullet, Graphics, Circle, Label, p50, DataItem, Line,
  Button,
  RoundedRectangle,
  p100,
} from '@amcharts/amcharts5';
import type {
  ClusteredPointSeries,
  IClusteredPointSeriesDataItem,
  IMapPointSeriesDataItem,
} from '@amcharts/amcharts5/map';
import { bulletOffsets, needBulletLines } from '../../constants';
import { colors } from '~/styles/colors';
import { t } from 'i18next';
import ResponsiveTheme from '@amcharts/amcharts5/themes/Responsive';
import { DataContextStateMarker, DataContextUnitMarker } from '../../types';

type BulletEvents = {
    onClick: (
        event: ISpritePointerEvent & {
            type: 'click';
            target: Container;
        }
    ) => void;
    onPointerOver: (
        event: ISpritePointerEvent & {
            type: 'pointerover';
            target: Container;
        }
    ) => void;
    onPointerOut: (
        event: ISpritePointerEvent & {
            type: 'pointerout';
            target: Container;
        }
    ) => void;
};

const pinSvg = 'M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z M12 22C16 18 20 14.4183 20 10C20 5.58172 16.4183 2 12 2C7.58172 2 4 5.58172 4 10C4 14.4183 8 18 12 22Z';

const flipSvg = 'M1.75 5.25033H9.625C11.0747 5.25033 12.25 6.42558 12.25 7.87533C12.25 9.32507 11.0747 10.5003 9.625 10.5003H7M1.75 5.25033L4.08333 2.91699M1.75 5.25033L4.08333 7.58366';

export function unitMarker(root: Root, dataItem: DataItem<IClusteredPointSeriesDataItem>, events: BulletEvents): Bullet {
  const bulletTemplate = Template.new<Graphics>({});
  const colorPin = (dataItem.dataContext as DataContextUnitMarker).color;

  const container = Container.new(root, {
    width: 18,
    height: 24,
  });

  const hitArea = Rectangle.new(root, {
    width: 18,
    height: 24,
    fill: color(colors.bg['surface-700']),
    fillOpacity: 0,
    centerX: percent(0),
    centerY: percent(0),
    tooltipText: '{title}',
    cursorOverStyle: 'pointer',
    interactive: true,
    tooltipY: 0,
  });

  const customPin = Graphics.new(
    root,
    {
      width: 24,
      height: 24,
      svgPath: pinSvg,
      stroke: color(colorPin),
      strokeOpacity: 0.9,
      strokeWidth: 2,
      lineCap: 'round',
      lineJoin: 'round',
      centerX: percent(0),
      centerY: percent(0),
    },
    bulletTemplate,
  );

  customPin.states.create('disabled', {
    stroke: color(colors.icon['disabled-300']),
  });

  customPin.states.create('default', {
    stroke: color(colorPin),
  });

  container.children.push(customPin);
  container.children.push(hitArea);

  container.events.on('click', events.onClick);
  container.events.on('pointerover', events.onPointerOver);
  container.events.on('pointerout', events.onPointerOut);

  return Bullet.new(root, {
    sprite: container,
  });
}

export function customCircle(root: Root): Circle {
  return Circle.new(root, {
    radius: 16.5,
    fill: color(colors.icon['primary-base']),
  });
}

export function customLabel(root: Root): Label {
  return Label.new(root, {
    centerX: p50,
    centerY: p50,
    fill: color(colors.text['white-0']),
    populateText: true,
    fontSize: '20px',
    fontWeight: '700',
    text: '{value}',
    oversizedBehavior: 'fit',
    maxWidth: 33,
  });
}

export function customGroupBullet(
  root: Root,
  pointSeries: ClusteredPointSeries,
): Bullet {
  const container = Container.new(root, {
    cursorOverStyle: 'pointer',
    maxWidth: 33,
    width: 33,
    interactive: true,
  });

  const circle = customCircle(root);
  const label = customLabel(root);

  circle.states.create('disabled', {
    fill: color(colors.icon['disabled-300']),
  });

  label.states.create('disabled', {
    fill: color(colors.text['soft-400']),
  });

  circle.states.create('default', {
    fill: color(colors.icon['primary-base']),
  });

  label.states.create('default', {
    fill: color(colors.icon['white-0']),
  });

  container.children.push(circle);
  container.children.push(label);

  container.events.on('click', (e) => {
    if (e.target.dataItem) {
      pointSeries.zoomToCluster(e.target.dataItem);
    }
  });

  return Bullet.new(root, {
    sprite: container,
  });
}

export function customStateBullet(
  root: Root,
  dataItem: DataItem<IMapPointSeriesDataItem>,
  events?: BulletEvents,
  disabled?: boolean,
): Bullet {
  const polygonId = dataItem.get('polygonId');
  const offset = polygonId ? bulletOffsets[polygonId] : undefined;
  const needLine = polygonId
    ? needBulletLines[polygonId as keyof typeof needBulletLines]
    : false;
  const isEmptyBullet = (dataItem.dataContext as DataContextStateMarker | undefined)?.empty;

  const container = Container.new(root, {
    cursorOverStyle: 'pointer',
    dx: offset ? offset.dx : undefined,
    dy: offset ? offset.dy : undefined,
    interactive: !!events && !isEmptyBullet,
  });

  if (needLine && offset) {
    const line = Line.new(root, {
      stroke: color(colors.icon['sub-500']),
      strokeWidth: 0.6,
      height: 38,
      dx: -needLine.dx,
      dy: -needLine.dy,
      centerX: 0,
      centerY: 0,
      rotation: needLine.rotation,
    });
    container.children.push(line);
  }

  if (isEmptyBullet) {
    const labelNoInfo = Label.new(root, {
      centerX: p50,
      dy: -60,
      fill: color(colors.text['sub-500']),
      populateText: true,
      fontSize: '12px',
      fontWeight: '400',
      text: t('semInformacoesLatitudeLongitude'),
      width: 40,
      breakWords: true,
      textAlign: 'center',
    });

    labelNoInfo.states.create('default', {
      fill: color(colors.text['sub-500']),
    });

    container.children.push(labelNoInfo);
  }

  const { circle, label } = customCircleLabelBullet(root);

  container.children.push(circle);
  container.children.push(label);

  if (disabled) {
    container.states.apply('disabled');
    circle.states.apply('disabled');
    label.states.apply('disabled');
  } else if (!isEmptyBullet) {
    if (events) {
      container.events.on('click', events.onClick);
      container.events.on('pointerover', events.onPointerOver);
      container.events.on('pointerout', events.onPointerOut);
    } else {
      container.states.apply('disabled');
      circle.states.apply('disabled');
      label.states.apply('disabled');
    }
  }

  return Bullet.new(root, {
    sprite: container,
  });
}

function customCircleLabelBullet(root: Root): {
  circle: Circle,
  label: Label
} {
  const circle = customCircle(root);
  const label = customLabel(root);

  circle.states.create('disabled', {
    fill: color(colors.icon['disabled-300']),
  });
  circle.states.create('disabledHover', {
    fill: color(colors.brand.primary.lighter),
  });

  label.states.create('disabled', {
    fill: color(colors.text['soft-400']),
  });
  label.states.create('disabledHover', {
    fill: color(colors.text['disabled-300']),
  });

  circle.states.create('default', {
    fill: color(colors.icon['primary-base']),
  });

  label.states.create('default', {
    fill: color(colors.icon['white-0']),
  });

  return {
    circle,
    label,
  };
}

export function customPlusIcon(root: Root): Graphics {
  return Graphics.new(root, {
    stroke: color(colors.icon['primary-base']),
    strokeWidth: 2,
    svgPath: 'M12 5V19M5 12H19',
    width: 24,
    height: 24,
    dx: -4,
    dy: -2,
  });
}

export function customMinusIcon(root: Root): Graphics {
  return Graphics.new(root, {
    stroke: color(colors.icon['primary-base']),
    strokeWidth: 2,
    svgPath: 'M5 12H19',
    width: 24,
    height: 24,
    dx: -4,
    dy: -2,
  });
}

export function buildCustomButtonStyles(button: Button):void {
  button.classNames.push('customZoomButton');

  button.get('background')?.setAll({
    fill: color(colors.bg['white-0']),
    stroke: color(colors.stroke['primary-base']),
    strokeWidth: 1,
    strokeOpacity: 1,
  });

  button.get('background')?.states.create('hover', {}).setAll({
    fill: color(colors.brand.primary.light),
    stroke: color(colors.brand.primary.light),
  });

  button.get('background')?.states.create('down', {}).setAll({
    fill: color(colors.bg['white-0']),
    stroke: color(colors.stroke['primary-base']),
  });

  button.get('background')?.states.create('active', {}).setAll({
    fill: color(colors.brand.primary.light),
  });
}

export function getResponsiveTheme(root: Root): ResponsiveTheme {
  const responsiveTheme = ResponsiveTheme.new(root);
  responsiveTheme.addRule({
    name: 'MapChart',
    relevant: (width): boolean => width !== 468,
    settings: {
      minZoomLevel: 0.79,
      homeZoomLevel: 0.79,
    },
  });

  responsiveTheme.addRule({
    name: 'MapChart',
    relevant: (width): boolean => width === 468,
    settings: {
      minZoomLevel: 1,
      homeZoomLevel: 1,
    },
  });

  return responsiveTheme;
}

export function backButton(root: Root, onClick: BulletEvents['onClick']): Container {
  const background = RoundedRectangle.new(root, {
    fill: color(colors.bg['white-0']),
    cornerRadiusBL: 4,
    cornerRadiusBR: 4,
    cornerRadiusTL: 4,
    cornerRadiusTR: 4,
    stroke: color(colors.stroke['soft-200']),
    strokeWidth: 1,
  });

  const backContainer = Container.new(root, {
    x: p100,
    dx: -200,
    dy: -60,
    y: p100,
    paddingLeft: 16,
    paddingRight: 16,
    cursorOverStyle: 'pointer',
    interactive: true,
    layout: root.horizontalLayout,
    background,
    visible: false,
    width: 146,
    height: 33,
  });

  const flipBackwardIcon = Graphics.new(root, {
    y: 8,
    width: 14,
    height: 14,
    svgPath: flipSvg,
    stroke: color(colors.icon['primary-base']),
    lineCap: 'round',
    lineJoin: 'round',
  });

  const label = Label.new(root, {
    y: 2,
    centerX: p50,
    fill: color(colors.text['main-900']),
    fontSize: '11px',
    fontWeight: '600',
    fontFamily: 'Inter',
    text: t('voltarAoPadrao'),
    width: 88,
    marginLeft: 12,
  });

  background.states.create('hover', {
    fill: color(colors.brand.primary.light),
  });

  background.states.create('default', {
    fill: color(colors.bg['white-0']),
  });

  backContainer.children.push(flipBackwardIcon);
  backContainer.children.push(label);

  backContainer.events.on('click', onClick);
  background.events.on('pointerover', (event) => {
    event.target.states.apply('hover');
  });
  background.events.on('pointerout', (event) => {
    event.target.states.apply('default');
  });

  return backContainer;
}
